import type {Announcements} from '@dnd-kit/core';

import {getItemAccessibleName} from '../../shared/accessibleName';
import type {DragProgress} from '../../shared/dragProgress';
import type {OrderingItem} from '../store';
import {indexAfterMove} from '../store';

const position = (index: number, total: number): string => `position ${index + 1} of ${total}`;

/** The index a `divider-<n>` droppable id names, or null for any other droppable. */
export const dividerIndexFrom = (
  droppableId: string | number | null | undefined
): number | null => {
  const id = droppableId?.toString() ?? '';
  if (!id.startsWith('divider-')) return null;
  const index = parseInt(id.split('-')[1], 10);
  return Number.isNaN(index) ? null : index;
};

/** The spoken name of the insert point at `dividerIndex` while the item at `itemIndex` is selected. */
export const insertPointLabel = (
  name: string,
  itemIndex: number,
  dividerIndex: number,
  total: number
): string => {
  const target = indexAfterMove(itemIndex, dividerIndex);
  return target === itemIndex
    ? `Keep ${name} at ${position(target, total)}`
    : `Move ${name} to ${position(target, total)}`;
};

export const selectedMessage = (name: string, index: number, total: number): string =>
  `${name} selected, ${position(index, total)}. Choose an insert position.`;

export const deselectedMessage = (name: string): string => `${name} deselected.`;

export const movedMessage = (
  name: string,
  fromIndex: number,
  toIndex: number,
  total: number
): string =>
  toIndex === fromIndex
    ? `${name} kept at ${position(toIndex, total)}.`
    : `${name} moved to ${position(toIndex, total)}.`;

const staysMessage = (name: string, index: number, total: number): string =>
  `${name} stays at ${position(index, total)}.`;

/**
 * dnd-kit announcements for the ordering list, spoken through DndContext's own live region.
 * They read `items` as rendered: DndContext runs our drop handler before it announces, so on
 * drop the store has already moved the item, but the pre-move order plus the divider gives the
 * same landing position. An id the list doesn't have is left unannounced.
 */
export const orderingAnnouncements = (
  items: OrderingItem[],
  progress: DragProgress = {hasLeftOrigin: false}
): Announcements => {
  const total = items.length;
  const describe = (activeId: string | number): {index: number; name: string} | null => {
    const index = items.findIndex((item) => item.id === activeId);
    return index === -1 ? null : {index, name: getItemAccessibleName(items[index].value)};
  };

  return {
    onDragStart: ({active}) => {
      const item = describe(active.id);
      if (!item) return undefined;
      progress.hasLeftOrigin = false;
      return `Picked up ${item.name}, ${position(item.index, total)}.`;
    },
    onDragOver: ({active, over}) => {
      const item = describe(active.id);
      if (!item) return undefined;
      const {index, name} = item;
      const divider = dividerIndexFrom(over?.id);
      if (divider === null) {
        progress.hasLeftOrigin = true;
        return `${name} is not over an insert position.`;
      }
      const target = indexAfterMove(index, divider);
      if (target === index && !progress.hasLeftOrigin) return undefined;
      progress.hasLeftOrigin = true;
      return target === index
        ? `${name} would stay at ${position(index, total)}.`
        : `${name} would move to ${position(target, total)}.`;
    },
    onDragEnd: ({active, over}) => {
      const item = describe(active.id);
      if (!item) return undefined;
      const {index, name} = item;
      const divider = dividerIndexFrom(over?.id);
      if (divider === null) return staysMessage(name, index, total);
      return movedMessage(name, index, indexAfterMove(index, divider), total);
    },
    onDragCancel: ({active}) => {
      const item = describe(active.id);
      if (!item) return undefined;
      return `Move cancelled. ${staysMessage(item.name, item.index, total)}`;
    }
  };
};
